import React, { useState } from 'react';
import { CheckCircle, AlertCircle, ChevronUp, ChevronDown } from 'lucide-react';

interface KYCSection {
  complete: boolean;
  last_updated?: string;
  missing_fields?: string[];
  [key: string]: any;
}

interface KYCSectionCardProps {
  title: string;
  section: KYCSection;
  icon: React.ReactNode;
}

const formatFieldName = (key: string): string => {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .replace(/\b\w/g, (str) => str.toUpperCase());
};

const KYCSectionCard: React.FC<KYCSectionCardProps> = ({ title, section, icon }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Map titles to simple IDs for scroll navigation (matching KYCSidebar)
  const titleToIdMap: Record<string, string> = {
    'Personal Information': 'personal',
    'Contact Information': 'contact',
    'Employment Information': 'employment',
    'Personal Background': 'background',
    'Tax Information': 'tax',
    'PEP & Adverse Media': 'pep',
    'Business Activities': 'business',
    'Wealth Sources': 'wealth',
    'Income Sources': 'income',
    'Documents': 'documents',
  };

  const sectionId = titleToIdMap[title] || title.toLowerCase().replace(/\s+/g, '-').replace(/[&]/g, '');

  const renderObjectDetails = (obj: any, index?: number): React.ReactNode => {
    const entries = Object.entries(obj).filter(([_, v]) => v !== null && v !== undefined && v !== '');

    if (entries.length === 0) return null;

    return (
      <div className="ml-4 pl-4 border-l-2 border-gray-200 space-y-1">
        {index !== undefined && (
          <div className="font-medium text-gray-700 text-xs mb-2">Item {index + 1}</div>
        )}
        {entries.map(([key, value]) => (
          <div key={key} className="text-xs">
            <span className="text-gray-600">{formatFieldName(key)}:</span>{' '}
            <span className="text-gray-900">
              {typeof value === 'boolean' ? (value ? 'Yes' : 'No') :
               Array.isArray(value) ? value.join(', ') :
               typeof value === 'object' ? JSON.stringify(value) :
               String(value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderFieldValue = (key: string, value: any): React.ReactNode => {
    if (value === null || value === undefined || value === '') {
      return <span className="font-medium text-gray-900">Not provided</span>;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return <span className="font-medium text-gray-900">Not provided</span>;

      // Check if array contains objects (like business activities, wealth sources, etc.)
      if (value.some(item => typeof item === 'object' && item !== null)) {
        return (
          <div className="col-span-2 space-y-3">
            <div className="text-gray-600 font-medium">{formatFieldName(key)} ({value.length} item{value.length !== 1 ? 's' : ''}):</div>
            {value.map((item, idx) => (
              <div key={idx}>
                {renderObjectDetails(item, idx)}
              </div>
            ))}
          </div>
        );
      }

      // Array of primitives (strings, numbers)
      return <span className="font-medium text-gray-900">{value.join(', ')}</span>;
    }
    if (typeof value === 'object') {
      // Render nested object details
      return (
        <div className="col-span-2">
          <div className="text-gray-600 font-medium mb-2">{formatFieldName(key)}:</div>
          {renderObjectDetails(value)}
        </div>
      );
    }
    if (typeof value === 'boolean') {
      return <span className="font-medium text-gray-900">{value ? 'Yes' : 'No'}</span>;
    }
    return <span className="font-medium text-gray-900">{String(value)}</span>;
  };

  const getDisplayFields = () => {
    const metadataKeys = [
      'complete', 'last_updated', 'missing_fields',
      'completeness', 'fieldCount', 'isArray',
    ];
    return Object.entries(section)
      .filter(([key]) => !metadataKeys.includes(key))
      .filter(([_, value]) => value !== null && value !== undefined);
  };

  const displayFields = getDisplayFields();

  return (
    <div id={sectionId} className="border border-gray-200 rounded-lg scroll-mt-6">
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <div className="text-gray-600">{icon}</div>
            <div className="flex-1">
              <h4 className="font-medium text-gray-900">{title}</h4>
              {section.missing_fields && section.missing_fields.length > 0 && (
                <p className="text-xs text-red-600 mt-0.5">
                  {section.missing_fields.length} field(s) missing
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {section.complete ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-amber-600" aria-label="Incomplete section" />
            )}

            {displayFields.length > 0 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
              >
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-600" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-600" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && displayFields.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-3 text-sm">
              {displayFields.map(([key, value]) => {
                const renderedValue = renderFieldValue(key, value);

                // Check if this is a complex field that needs full width (arrays of objects)
                const isComplexField = Array.isArray(value) && value.some(item => typeof item === 'object' && item !== null);
                const isObjectField = typeof value === 'object' && !Array.isArray(value) && value !== null;

                if (isComplexField || isObjectField) {
                  return <div key={key} className="col-span-2">{renderedValue}</div>;
                }

                return (
                  <div key={key} className="break-words">
                    <span className="text-gray-600">{formatFieldName(key)}:</span>{' '}
                    {renderedValue}
                  </div>
                );
              })}
            </div>

            {section.missing_fields && section.missing_fields.length > 0 && (
              <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-xs text-yellow-800">
                  <span className="font-medium">Missing fields:</span> {section.missing_fields.join(', ')}
                </p>
              </div>
            )}
          </div>
        )}

        {/* No data message */}
        {isExpanded && displayFields.length === 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-500 text-center italic">
              No information available for this section
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KYCSectionCard;
